import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Header } from "./components/Header";
import { useParams } from "react-router-dom";
import { NavLink } from "react-router-dom";
import { ShimmerTable } from "react-shimmer-effects";
import toast, { Toaster } from "react-hot-toast";
import Navbar from "./components/Navbar";
import { loadAll } from "./CriteriaService";
const API_URL = "/api";

export default function FinancialAnalysis() {
  const { id } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [criteriaFiles, setCriteriaFiles] = useState([]);
  const [selectedCriteria, setSelectedCriteria] = useState("");

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/getComapnyProfile/${id}`);
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setCompany(data);
        setFormData(data);
        console.log("data:", data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyDetails();
  }, [id]);

  useEffect(() => {
    if (company?.COMPANYINFO?.COMPANYNAME_ENGLISH) {
      document.title = company.COMPANYINFO.COMPANYNAME_ENGLISH;
    } else {
      document.title = "Company Log"; // fallback
    }
    return () => {
      document.title = "Company Log";
    };
  }, [company]);
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const all = await loadAll();
        const names = all.map((r) => r.saveName || "Untitled");
        setCriteriaFiles(names);
      } catch (err) {
        console.error("Error fetching criteria files:", err);
      }
    };
    fetchFiles();
  }, []);

  const handleChange = (category, year, value, section) => {
    const inferredSection =
      section ||
      ([
        "REVENUE",
        "LESSCOSTOFSALES",
        "EBITDA",
        "DA",
        "INCOMETAXEXPENSE",
        "FINANCECOSTS",
      ].includes(category)
        ? "INCOMESTATEMENT"
        : "BALANCESHEET");

    setFormData((prev) => {
      const updated = { ...prev };
      if (!updated.FINANCIALANALYSIS) updated.FINANCIALANALYSIS = {};
      if (!updated.FINANCIALANALYSIS[inferredSection])
        updated.FINANCIALANALYSIS[inferredSection] = {};
      if (!updated.FINANCIALANALYSIS[inferredSection][category])
        updated.FINANCIALANALYSIS[inferredSection][category] = {};
      updated.FINANCIALANALYSIS[inferredSection][category][year] =
        Number(value);
      return updated;
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        COMPANYID: company.COMPANYID,
        update_data: formData,
      };

      const response = await fetch(
        `${API_URL}/updateCompanyProfile?username=pritomrajkhowa`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to update");

      await response.json();
      toast.success("Financials updated");
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData(company);
    setIsEditing(false);
  };

  const handleBalanceInputChange = (e, key, year, section) => {
    const inferredSection = section || "BALANCESHEET";
    const value = parseFloat(e.target.value.replace(/[^0-9.-]+/g, "")) || 0;

    setFormData((prev) => {
      const updated = { ...prev };
      if (!updated.FINANCIALANALYSIS) updated.FINANCIALANALYSIS = {};
      if (!updated.FINANCIALANALYSIS[inferredSection])
        updated.FINANCIALANALYSIS[inferredSection] = {};
      if (!updated.FINANCIALANALYSIS[inferredSection][key])
        updated.FINANCIALANALYSIS[inferredSection][key] = {};
      updated.FINANCIALANALYSIS[inferredSection][key][year] = value;
      return updated;
    });
  };

  const computeGrossProfit = (year) => {
    const revenue =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.REVENUE?.[year] ?? 0;
    const cost =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.LESSCOSTOFSALES?.[year] ??
      0;
    return revenue + cost;
  };

  const computeEBITDA = (year) => {
    const grossProfit = computeGrossProfit(year);
    const sga =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.SGANDAEXPENSES?.[year] ?? 0;
    const externalCosts =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT
        ?.EXTERNALOPERATINGCOSTS_INCLDISTRIBUTIONRANDDSERVICES?.[year] ?? 0;
    const otherTaxes =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.TAXESOTHERTHANINCOMETAX?.[
        year
      ] ?? 0;
    return grossProfit + sga + externalCosts + otherTaxes;
  };

  const computeProfitBeforeTax = (year) => {
    const ebitda = computeEBITDA(year);
    const danda =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.DANDA?.[year] ?? 0;
    const provisions =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT
        ?.PROVISIONSANDIMPAIRMENTLOSSES?.[year] ?? 0;
    const disposalsPPE =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT
        ?.DISPOSALSOFPPE_GAINSORLOSSES?.[year] ?? 0;
    const disposalsIntangibles =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT
        ?.DISPOSALSOFINTANGIBLESSUBSIDIARIESANDAFFILIATES_GAINSORLOSSES?.[
        year
      ] ?? 0;
    const gainsECL =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT
        ?.GAINSOR_LOSSESRELATEDTOECLCREDITMODEL?.[year] ?? 0;
    const otherIncome =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT
        ?.OTHERNON_RECURRINGINCOMEOREXPENSES?.[year] ?? 0;
    const financeCosts =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.FINANCECOSTS?.[year] ?? 0;
    return (
      ebitda +
      danda +
      provisions +
      disposalsPPE +
      disposalsIntangibles +
      gainsECL +
      otherIncome +
      financeCosts
    );
  };

  // const formatIndianCurrency = (value) => {
  //   if (typeof value !== "number") return "0.00";
  //   return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  // };

  function formatDateMM(dateString) {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date)) return "N/A"; // Handle invalid date
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  const formatIndianCurrency = (value) => {
    if (typeof value !== "number") return "0";
    return Math.round(value)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const years = [2021, 2022, 2023, 2024, 2025];
  const auditorOption =
    company?.FINANCIALANALYSIS?.Auditor_Option?.ENTERAUDITOROPINION &&
    company?.FINANCIALANALYSIS?.Auditor_Option?.ENTERAUDITOROPINION;

  console.log(auditorOption);
  const editableCategories = [
    "REVENUE",
    "LESSCOSTOFSALES",
    "INCOMETAXEXPENSE",
    "SGANDAEXPENSES",
    "EXTERNALOPERATINGCOSTS_INCLDISTRIBUTIONRANDDSERVICES",
    "TAXESOTHERTHANINCOMETAX",
    "DANDA",
    "PROVISIONSANDIMPAIRMENTLOSSES",
    "DISPOSALSOFPPE_GAINSORLOSSES",
    "DISPOSALSOFINTANGIBLESSUBSIDIARIESANDAFFILIATES_GAINSORLOSSES",
    "GAINSOR_LOSSESRELATEDTOECLCREDITMODEL",
    "OTHERNON_RECURRINGINCOMEOREXPENSES",
    "FINANCECOSTS",
  ];

  const renderCell = (category, year) => {
    const value =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.[category]?.[year] ?? "";
    if (isEditing && editableCategories.includes(category)) {
      return (
        <input
          type="number"
          className="form-control"
          value={value}
          onChange={(e) =>
            handleChange(category, year, e.target.value, "INCOMESTATEMENT")
          }
        />
      );
    }
    if (typeof value !== "number") return "";
    const formatted = formatIndianCurrency(Math.abs(value));
    return value < 0 ? `(${formatted})` : formatted;
  };

  const renderAuditorOpinionCell = (year) => {
    const value =
      formData?.FINANCIALANALYSIS?.Auditor_Option?.AUDITOROPINION?.[year] ?? "";

    if (isEditing) {
      return (
        <input
          type="text"
          className="form-control"
          value={value}
          onChange={(e) =>
            handleChange(
              "Auditor_Option.AUDITOROPINION",
              year,
              e.target.value,
              "FINANCIALANALYSIS"
            )
          }
        />
      );
    }

    return value;
  };

  const categories = [
    { key: "REVENUE", label: "Revenue" },
    { key: "LESSCOSTOFSALES", label: "Less: Cost of sales" },
  ];

  const balanceSheetCategories = [
    {
      key: "PROPERTYPLANTANDEQUIPMENT",
      label: "Property, plant and equipment",
    },
    { key: "RIGHT_OF_USEASSETS", label: "Right-of-use assets" },
    { key: "INVESTMENTPROPERTIES", label: "Investment properties" },
    {
      key: "INVESTMENTSOREQUITYAFFILIATES",
      label: "Investments / equity affiliates",
    },
    {
      key: "OTHERNON_CURRENTASSETS_INCLPROVISIONSETC",
      label: "Other non-current assets (incl provisions, etc)",
    },
  ];

  const currentAssetsCategories = [
    { key: "TRADERECEIVABLES", label: "Trade receivables" },
    { key: "RETENTIONRECEIVABLES", label: "Retention receivables" },
    {
      key: "GROSSAMOUNTSDUEFROMCUSTOMERFORCONTRACTWORKS",
      label: "Gross amounts due from customer for contract works",
    },
    { key: "INVENTORIES", label: "Inventories" },
    {
      key: "DEPOSITSPREPAYMENTSANDOTHERRECEIVABLES",
      label: "Deposits, prepayments and other receivables",
    },
    { key: "DUEFROMDIRECTORS", label: "Due from directors" },
    { key: "DUEFROMRELATEDCOMPANIES", label: "Due from related companies" },
    { key: "DUEFROMSHAREHOLDERS", label: "Due from shareholders" },
    { key: "INVESTMENTINQUOTEDSHARES", label: "Investment in quoted shares" },
    { key: "CASHANDBANKBALANCES", label: "Cash and bank balances" },
    { key: "OTHERCURRENTASSETS", label: "Other current assets" },
    { key: "CURRENTTAXRECOVERABLE", label: "Current tax recoverable" },
  ];

  const currentLiabilitiesCategories = [
    { key: "TRADEPAYABLES_CURRENT", label: "Trade payables" },
    {
      key: "GROSSAMOUNTSDUETOCUSTOMERSFORCONTRACTWORKS_CURRENT",
      label: "Gross amounts due to customers for contract works",
    },
    {
      key: "OTHERPAYABLESANDACCRUALS_CURRENT",
      label: "Other payables and accruals",
    },
    { key: "DUETODIRECTORS_CURRENT", label: "Due to directors" },
    { key: "DUETORELATEDCOMPANIES_CURRENT", label: "Due to related companies" },
    { key: "DUETOSHAREHOLDERS_CURRENT", label: "Due to shareholders" },
    {
      key: "OBLIGATIONUNDERFINANCELEASES_CURRENT",
      label: "Obligation under finance leases",
    },
    { key: "BANKOVERDRAFT_CURRENT", label: "Bank overdraft" },

    { key: "BANKBORROWINGS_CURRENT", label: "Bank borrowings" },
    {
      key: "OTHERCURRENTLIABILITIES_CURRENT",
      label: "Other current liabilities",
    },
    { key: "CURRENTTAXLIABILITIES_CURRENT", label: "Current tax liabilities" },
  ];

  const nonCurrentLiabilitiesCategories = [
    {
      key: "OBLIGATIONUNDERFINANCELEASES_NONCURRENT",
      label: "Obligation under finance leases",
    },
    {
      key: "BANKBORROWINGS_NONCURRENT",
      label: "Bank borrowings",
    },
    {
      key: "OTHERNON_CURRENTLIABILITIES_NONCURRENT",
      label: "Other non-current liabilities",
    },
  ];
  const equityCategories = [
    { key: "SHARECAPITAL", label: "Share capital" },
    { key: "ACCUMULATEDPROFITS", label: "Accumulated profits" },
  ];

  const cashflowCategories = {
    _PURCHASESORDISPOSALSOFPROPERTYPLANTANDEQUIPMENT:
      "Purchases/ Disposals of Property Plant & Equipment",
    _PURCHASESORDISPOSALSOFINVESTMENTS: "Purchases/ Disposals of Investments",
    _PURCHASESORDISPOSALSOFRIGHT_OF_USEASSETS:
      "Purchases/ Disposals of Right-of-use Assets",
  };

  const cashflowFromFinancingActivities = {
    CHANGESINAMOUNTSDUEORFROMDIRECTORSANDRELATEDPARTIES:
      "Changes in Amounts due/from Directors and Related Parties",
    DRAWDOWNSOR_REPAYMENTSOFBORROWINGS: "Drawdowns/ (Repayments) of Borrowings",
    INTERESTPAID: "Interest Paid",
    CHANGESINCONTRIBUTEDCAPITAL: "Changes in Contributed Capital",
    PAYMENTSOFDIVIDENDS: "Payments of Dividends",
  };

  const renderEditableBalanceCell = (key, year) => {
    let categorySource;
    let actualKey = key;

    if (key.endsWith("_CURRENT")) {
      categorySource = "CURRENTLIABILITIES";
      actualKey = key.replace("_CURRENT", "");
    } else if (key.endsWith("_NONCURRENT")) {
      categorySource = "NONCURRENTLIABILITIES";
      actualKey = key.replace("_NONCURRENT", "");
    } else if (equityCategories.some((cat) => cat.key === key)) {
      categorySource = "EQUITY";
    } else {
      categorySource = "BALANCESHEET";
    }

    const value =
      formData?.FINANCIALANALYSIS?.[categorySource]?.[actualKey]?.[year] ?? "";

    if (isEditing) {
      return (
        <input
          type="text"
          value={value}
          onChange={(e) =>
            handleBalanceInputChange(e, actualKey, year, categorySource)
          }
          style={{
            width: "100%",
            border: "1px solid #ccc",
            textAlign: "right",
            paddingRight: "4px",
          }}
        />
      );
    }

    if (typeof value !== "number") return "";
    const formatted = formatIndianCurrency(Math.abs(value));
    return value < 0 ? `(${formatted})` : formatted;
  };

  const computeProfitForYear = (year) => {
    const profitBeforeTax = computeProfitBeforeTax(year);
    const tax =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.INCOMETAXEXPENSE?.[year] ??
      0;
    return profitBeforeTax + tax;
  };

  const computeTotalNonCurrentAssets = (year) => {
    const categories = [
      "PROPERTYPLANTANDEQUIPMENT",
      "RIGHT_OF_USEASSETS",
      "INVESTMENTPROPERTIES",
      "INVESTMENTSOREQUITYAFFILIATES",
      "OTHERNON_CURRENTASSETS_INCLPROVISIONSETC",
    ];
    return categories.reduce((total, cat) => {
      return (
        total + (formData?.FINANCIALANALYSIS?.BALANCESHEET?.[cat]?.[year] ?? 0)
      );
    }, 0);
  };

  const computeTotalCurrentAssets = (year) => {
    return currentAssetsCategories.reduce((total, cat) => {
      return (
        total +
        (formData?.FINANCIALANALYSIS?.BALANCESHEET?.[cat.key]?.[year] ?? 0)
      );
    }, 0);
  };

  const computeTotalCurrentLiabilities = (year) => {
    return currentLiabilitiesCategories.reduce((total, cat) => {
      const rawKey = cat.key.replace("_CURRENT", "");
      const value =
        formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.[rawKey]?.[year] ?? 0;
      return total + value;
    }, 0);
  };

  const computeNetCurrentAssets = (year) => {
    return (
      computeTotalCurrentAssets(year) - computeTotalCurrentLiabilities(year)
    );
  };

  const computeTotalNonCurrentLiabilities = (year) => {
    return nonCurrentLiabilitiesCategories.reduce((total, cat) => {
      const rawKey = cat.key.replace("_NONCURRENT", ""); // adjust to actual key in data
      const value =
        formData?.FINANCIALANALYSIS?.NONCURRENTLIABILITIES?.[rawKey]?.[year] ??
        0;
      return total + value;
    }, 0);
  };

  const computeNetAssets = (year) => {
    const totalNonCurrentAssets = computeTotalNonCurrentAssets(year);
    const totalCurrentAssets = computeTotalCurrentAssets(year);
    const totalCurrentLiabilities = computeTotalCurrentLiabilities(year);
    const totalNonCurrentLiabilities = computeTotalNonCurrentLiabilities(year);
    return (
      totalNonCurrentAssets +
      totalCurrentAssets -
      totalCurrentLiabilities -
      totalNonCurrentLiabilities
    );
  };

  const computeTotalShareholdersEquity = (year) => {
    const shareCapital =
      formData?.FINANCIALANALYSIS?.EQUITY?.SHARECAPITAL?.[year] ?? 0;
    const accumulatedProfits =
      formData?.FINANCIALANALYSIS?.EQUITY?.ACCUMULATEDPROFITS?.[year] ?? 0;
    return shareCapital + accumulatedProfits;
  };

  const computeCheckDifference = (year) => {
    return computeTotalShareholdersEquity(year) - computeNetAssets(year);
  };

  const computeNegativeDA = (year) => {
    const da = formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.DA?.[year] ?? 0;
    return -da;
  };

  const computeDA = (year) => {
    const da =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT?.DANDA?.[year] ?? 0;
    return da;
  };
  const computeNegativeFC = (year) => {
    const fc =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.FINANCECOSTS?.[year] ?? 0;
    return -fc;
  };

  const computeFC = (year) => {
    const fc =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT?.FINANCECOSTS?.[year] ?? 0;
    return fc;
  };
  const computeNegativeGLI = (year) => {
    const gli =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT
        ?.DISPOSALSOFPPE_GAINSORLOSSES?.[year] ?? 0;
    return -gli;
  };

  const computeGLI = (year) => {
    const gli =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT
        ?._ORGAINSOR_LOSSESONIMPAIRMENTORDISPOSALOFPROPERTYPLANTANDEQUIPMENT?.[
        year
      ] ?? 0;
    return gli;
  };

  const computeNegativeRDI = (year) => {
    const rdi =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT
        ?.DISPOSALSOFINTANGIBLESSUBSIDIARIESANDAFFILIATES_GAINSORLOSSES?.[
        year
      ] ?? 0;
    return -rdi;
  };

  const computeRDI = (year) => {
    const rdi =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT
        ?._ORGAINSOR_LOSSESONREVALUATIONORDISPOSALOFINVESTMENTS?.[year] ?? 0;
    return rdi;
  };

  const computeNegativeED = (year) => {
    const ed =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT
        ?.GAINSOR_LOSSESRELATEDTOECLCREDITMODEL?.[year] ?? 0;
    return -ed;
  };

  const computeED = (year) => {
    const ed =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT
        ?._ORGAINSOR_LOSSESONEXCHANGEDIFFERENCE?.[year] ?? 0;
    return ed;
  };

  const computeCashflowBeforeExchange = (year) => {
    return (
      computeProfitForYear(year) +
      computeNegativeDA(year) +
      computeNegativeFC(year) +
      computeNegativeGLI(year) +
      computeNegativeRDI(year) +
      computeNegativeED(year)
    );
  };

  const computeCashflowBeforeExchangeFromData = (year) => {
    return (
      computeProfitForYear(year) +
      computeDA(year) +
      computeFC(year) +
      computeGLI(year) +
      computeRDI(year) +
      computeED(year)
    );
  };

  const computeChangesInAR = (year) => {
    const prevYear = year - 1;
    const keys = [
      "TRADERECEIVABLES",
      "RETENTIONRECEIVABLES",
      "GROSSAMOUNTSDUEFROMCUSTOMERFORCONTRACTWORKS",
      "INVENTORIES",
      "DEPOSITSPREPAYMENTSANDOTHERRECEIVABLES",
    ];
    const currentSum = keys.reduce((sum, key) => {
      return (
        sum + (formData?.FINANCIALANALYSIS?.BALANCESHEET?.[key]?.[year] ?? 0)
      );
    }, 0);
    const prevSum = keys.reduce((sum, key) => {
      return (
        sum +
        (formData?.FINANCIALANALYSIS?.BALANCESHEET?.[key]?.[prevYear] ?? 0)
      );
    }, 0);
    return prevSum - currentSum;
  };

  const computeChangesInARFromData = (year) => {
    const ed =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT?.OR_CHANGESINAR?.[year] ??
      0;
    return ed;
  };

  const computeChangesInCL = (year) => {
    const prevYear = year - 1;
    const keys = [
      "TRADEPAYABLES",
      "GROSSAMOUNTSDUETOCUSTOMERSFORCONTRACTWORKS",
      "OTHERPAYABLESANDACCRUALS",
    ];
    const currentSum = keys.reduce((sum, key) => {
      return (
        sum +
        (formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.[key]?.[year] ?? 0)
      );
    }, 0);
    const prevSum = keys.reduce((sum, key) => {
      return (
        sum +
        (formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.[key]?.[prevYear] ??
          0)
      );
    }, 0);
    return currentSum - prevSum;
  };

  const computeChangesInCLFromData = (year) => {
    const ed =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT?.OR_CHANGESISAP?.[year] ??
      0;
    return ed;
  };

  const computeARandAP = (year) => {
    return (
      computeCashflowBeforeExchange(year) +
      computeChangesInAR(year) +
      computeChangesInCL(year)
    );
  };

  const computeARandAPFromData = (year) => {
    const ed =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT
        ?.CASHFLOWFROMOPERATINGACTIVITIES?.[year] ?? 0;
    return ed;
  };
  const computeCashflowFromInvestingActivities = (year) => {
    const purchaed =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT
        ?._PURCHASESORDISPOSALSOFPROPERTYPLANTANDEQUIPMENT?.[year] ?? 0;
    const orDis =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT
        ?._PURCHASESORDISPOSALSOFINVESTMENTS?.[year] ?? 0;

    const useAssets =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT
        ?._PURCHASESORDISPOSALSOFRIGHT_OF_USEASSETS?.[year] ?? 0;

    return purchaed + orDis + useAssets;
  };

  const computeCashflowFromFinancingActivities = (year) => {
    const changesInAmountDue =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT
        ?.CHANGESINAMOUNTSDUEORFROMDIRECTORSANDRELATEDPARTIES?.[year] ?? 0;
    const drowDowns =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT
        ?.DRAWDOWNSOR_REPAYMENTSOFBORROWINGS?.[year] ?? 0;

    const interestPaid =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT?.INTERESTPAID?.[year] ?? 0;

    const contributedCapital =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT
        ?.CHANGESINCONTRIBUTEDCAPITAL?.[year] ?? 0;

    const paymentDevident =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT?.PAYMENTSOFDIVIDENDS?.[
        year
      ] ?? 0;

    return (
      changesInAmountDue +
      drowDowns +
      interestPaid +
      contributedCapital +
      paymentDevident
    );
  };

  const CashflowForTheYear = (year) => {
    return (
      computeCashflowFromFinancingActivities(year) +
      computeCashflowFromInvestingActivities(year) +
      computeARandAP(year)
    );
  };

  const CashflowForTheYearFromData = (year) => {
    const ed =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT?.CASHFLOWFORTHEYEAR?.[
        year
      ] ?? 0;
    return ed;
  };

  const computeChangesInCashAndBankBalances = (year) => {
    const prevYear = year - 1;
    const current =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.CASHANDBANKBALANCES?.[year] ??
      0;
    const previous =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.CASHANDBANKBALANCES?.[
        prevYear
      ] ?? 0;
    return current - previous;
  };

  const computeChangesInCashAndBankBalancesFromData = (year) => {
    const ed =
      formData?.FINANCIALANALYSIS?.CASHFLOWSTATEMENT?.CHANGEINCASHBALANCES?.[
        year
      ] ?? 0;
    return ed;
  };

  const computeRevenueGrowthRate = (year) => {
    const prevYear = year - 1;
    const currentRevenueRaw =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.REVENUE?.[year];
    const prevRevenueRaw =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.REVENUE?.[prevYear];

    const currentRevenue = parseFloat(currentRevenueRaw) || 0;
    const prevRevenue = parseFloat(prevRevenueRaw) || 0;

    if (
      prevRevenue === 0 ||
      !isFinite(currentRevenue) ||
      !isFinite(prevRevenue)
    ) {
      return "";
    }

    const ratio = currentRevenue / prevRevenue;

    if (ratio === 0 || !isFinite(ratio)) {
      return "";
    }

    const rate = (ratio - 1) * 100;
    return `${rate.toFixed(2)}%`;
  };

  const computeSquareRootRevenueGrowth = (year) => {
    const prevYear = year - 2;
    const currentRevenue =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.REVENUE?.[year] ?? 0;
    const baseRevenue =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.REVENUE?.[prevYear] ?? 0;

    if (baseRevenue === 0 || currentRevenue === 0) return "0.00%";

    const result = Math.sqrt(currentRevenue / baseRevenue) - 1;
    return (result * 100).toFixed(2) + "%";
  };

  const GrossProfitMargin = (year) => {
    const gross = computeGrossProfit(year);
    const revenue =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.REVENUE?.[year] ?? 0;

    if (revenue === 0) return "0.00%";

    const result = (gross / revenue) * 100;
    return result.toFixed(2) + "%";
  };

  const computeExcelStyleAverageGPM = (years) => {
    const results = [];

    for (let i = 0; i < years.length; i++) {
      let values = [];

      if (i <= 2) {
        // --- cumulative average up to this year ---
        for (let j = 0; j <= i; j++) {
          const val = parseFloat(GrossProfitMargin(years[j]));
          if (!isNaN(val) && val !== 0) values.push(val);
        }
      } else if (i === years.length - 1) {
        // --- last year (e.g. 2025) → use all years but skip 0 ---
        for (let j = 0; j <= i; j++) {
          const val = parseFloat(GrossProfitMargin(years[j]));
          if (!isNaN(val) && val !== 0) values.push(val);
        }
      } else {
        // --- rolling 3-year average (e.g. 2024) ---
        for (let j = i - 2; j <= i; j++) {
          const val = parseFloat(GrossProfitMargin(years[j]));
          if (!isNaN(val) && val !== 0) values.push(val);
        }
      }

      const avg = values.length
        ? (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2) + "%"
        : "";
      results.push(avg);
    }

    return results;
  };

  const ebitdaMargin = (year) => {
    const ebitda = computeEBITDA(year);
    const revenue =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.REVENUE?.[year] ?? 0;

    if (ebitda === 0 || revenue === 0) return "0.00%";

    const result = ebitda / revenue;
    return (result * 100).toFixed(2) + "%";
  };

  const NetProfitMargin = (year) => {
    const ProfitLossbeforetax = computeProfitBeforeTax(year);
    const revenue =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.REVENUE?.[year] ?? 0;

    if (ProfitLossbeforetax === 0 || revenue === 0) return "0.00%";

    const result = ProfitLossbeforetax / revenue;
    return (result * 100).toFixed(2) + "%";
  };

  const CurrentRatio = (year) => {
    const currentAssets = computeTotalCurrentAssets(year);

    const directors =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.DUEFROMDIRECTORS?.[year] ?? 0;

    const companies =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.DUEFROMRELATEDCOMPANIES?.[
        year
      ] ?? 0;

    const shareholders =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.DUEFROMSHAREHOLDERS?.[year] ??
      0;

    const CurrentLiabilities = computeTotalCurrentLiabilities(year);

    const directorsTo =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.DUETODIRECTORS?.[year] ??
      0;

    const companiesTo =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.DUETORELATEDCOMPANIES?.[
        year
      ] ?? 0;

    const shareholdersTo =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.DUETOSHAREHOLDERS?.[
        year
      ] ?? 0;

    const sum = directors + companies + shareholders;

    const sumTo = directorsTo + companiesTo + shareholdersTo;
    const firstdivide = currentAssets - sum;
    const seconddivide = CurrentLiabilities - sumTo;

    const isValid =
      !isNaN(firstdivide) && !isNaN(seconddivide) && seconddivide !== 0;

    const result = isValid ? (firstdivide / seconddivide).toFixed(2) : "00.00";

    return result;
  };

  const QuickRatio = (year) => {
    const cash =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.CASHANDBANKBALANCES?.[year] ??
      0;

    const tax =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.CURRENTTAXRECOVERABLE?.[
        year
      ] ?? 0;

    const trade =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.TRADERECEIVABLES?.[year] ?? 0;

    const firstSum = cash + tax + trade;

    const TotalCurrentLiabilities = computeTotalCurrentLiabilities(year);

    const directorsTo =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.DUETODIRECTORS?.[year] ??
      0;

    const companiesTo =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.DUETORELATEDCOMPANIES?.[
        year
      ] ?? 0;

    const shareholdersTo =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.DUETOSHAREHOLDERS?.[
        year
      ] ?? 0;

    const secondSum = directorsTo + companiesTo + shareholdersTo;

    const final = TotalCurrentLiabilities - secondSum;
    const isValid = !isNaN(firstSum) && final !== 0;

    const result = isValid ? (firstSum / final).toFixed(2) : "00.00";

    return result;
  };

  const CurrentAssetsfromRelatedParties = (year) => {
    const directors =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.DUEFROMDIRECTORS?.[year] ?? 0;

    const companies =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.DUEFROMRELATEDCOMPANIES?.[
        year
      ] ?? 0;

    const shareholders =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.DUEFROMSHAREHOLDERS?.[year] ??
      0;

    const sum = directors + companies + shareholders;

    const TotalCurrentAssets = computeTotalCurrentAssets(year);

    const result = sum / TotalCurrentAssets;
    const isValid = !isNaN(sum) && TotalCurrentAssets !== 0;

    return isValid ? (result * 100).toFixed(2) + "%" : "00.00";
  };

  const DebtEquityRatio = (year) => {
    const obligation =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES
        ?.OBLIGATIONUNDERFINANCELEASES?.[year] ?? 0;

    const bankOverDraft =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.BANKOVERDRAFT?.[year] ??
      0;

    const bankBorrowing =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.BANKBORROWINGS?.[year] ??
      0;

    const obligationNon =
      formData?.FINANCIALANALYSIS?.NONCURRENTLIABILITIES
        ?.OBLIGATIONUNDERFINANCELEASES?.[year] ?? 0;

    const bankBorrowingNon =
      formData?.FINANCIALANALYSIS?.NONCURRENTLIABILITIES?.BANKBORROWINGS?.[
        year
      ] ?? 0;

    const TotalShareholdersEquity = computeTotalShareholdersEquity(year);

    const sum =
      obligation +
      bankOverDraft +
      bankBorrowing +
      obligationNon +
      bankBorrowingNon;

    const isValid = !isNaN(sum) && TotalShareholdersEquity !== 0;

    const result = isValid ? sum / TotalShareholdersEquity : "00.00";

    return result;

    // return (result * 100).toFixed(2) + "%";
  };

  const NetDebtEBITDARatio = (year) => {
    const obligation =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES
        ?.OBLIGATIONUNDERFINANCELEASES?.[year] ?? 0;

    const bankOverDraft =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.BANKOVERDRAFT?.[year] ??
      0;

    const bankBorrowing =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.BANKBORROWINGS?.[year] ??
      0;

    const obligationNon =
      formData?.FINANCIALANALYSIS?.NONCURRENTLIABILITIES
        ?.OBLIGATIONUNDERFINANCELEASES?.[year] ?? 0;

    const bankBorrowingNon =
      formData?.FINANCIALANALYSIS?.NONCURRENTLIABILITIES?.BANKBORROWINGS?.[
        year
      ] ?? 0;

    const cash =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.CASHANDBANKBALANCES?.[year] ??
      0;

    const EBITDA = computeEBITDA(year);

    const sum =
      obligation +
      bankOverDraft +
      bankBorrowing +
      obligationNon +
      bankBorrowingNon -
      cash;

    const isValid = !isNaN(sum) && EBITDA !== 0;

    const result = isValid ? (sum / EBITDA).toFixed(2) : "00.00";

    return result;

    // return (result * 100).toFixed(2) + "%";
  };

  const DebtAssetsRatio = (year) => {
    const obligation =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES
        ?.OBLIGATIONUNDERFINANCELEASES?.[year] ?? 0;

    const bankOverDraft =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.BANKOVERDRAFT?.[year] ??
      0;

    const bankBorrowing =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.BANKBORROWINGS?.[year] ??
      0;

    const obligationNon =
      formData?.FINANCIALANALYSIS?.NONCURRENTLIABILITIES
        ?.OBLIGATIONUNDERFINANCELEASES?.[year] ?? 0;

    const bankBorrowingNon =
      formData?.FINANCIALANALYSIS?.NONCURRENTLIABILITIES?.BANKBORROWINGS?.[
        year
      ] ?? 0;

    const sum =
      obligation +
      bankOverDraft +
      bankBorrowing +
      obligationNon +
      bankBorrowingNon;

    const TotalCurrentAssets = computeTotalCurrentAssets(year);
    const TotalNonCurrentAssets = computeTotalNonCurrentAssets(year);
    const directors =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.DUEFROMDIRECTORS?.[year] ?? 0;

    const companies =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.DUEFROMRELATEDCOMPANIES?.[
        year
      ] ?? 0;

    const shareholders =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.DUEFROMSHAREHOLDERS?.[year] ??
      0;

    const sum2 =
      TotalCurrentAssets +
      TotalNonCurrentAssets -
      (directors + companies + shareholders);

    const isValid = !isNaN(sum) && sum2 !== 0;

    const result = isValid ? (sum / sum2).toFixed(2) : "00.00";

    return result;

    // return (result * 100).toFixed(2) + "%";
  };

  const InterestCoverageRatio = (year) => {
    const EBITDA = computeEBITDA(year);

    const financeCost =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.FINANCECOSTS?.[year] ?? 0;

    const isValid = !isNaN(EBITDA) && financeCost !== 0;

    const result = isValid ? (EBITDA / -financeCost).toFixed(2) : "00.00";

    return result;
  };

  const DaysInventoryOutstanding = (year) => {
    const inventories =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.INVENTORIES?.[year] ?? 0;

    const constOfSales =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.LESSCOSTOFSALES?.[year] ??
      0;

    const isValid = !isNaN(inventories) && constOfSales !== 0;
    const result = isValid
      ? ((inventories / -constOfSales) * 365).toFixed(2)
      : "0";

    return result;
  };

  const DaysSalesOutstanding = (year) => {
    const traderv =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.TRADERECEIVABLES?.[year] ?? 0;

    const retention =
      formData?.FINANCIALANALYSIS?.BALANCESHEET?.RETENTIONRECEIVABLES?.[year] ??
      0;

    const revenue =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.REVENUE?.[year] ?? 0;

    const sum = traderv + retention;

    const isValid = !isNaN(sum) && revenue !== 0;

    const result = isValid ? ((sum / revenue) * 365).toFixed(2) : 0;

    return result;
  };

  const DaysPayableOutstanding = (year) => {
    const tradepay =
      formData?.FINANCIALANALYSIS?.CURRENTLIABILITIES?.TRADEPAYABLES?.[year] ??
      0;

    const constOfSales =
      formData?.FINANCIALANALYSIS?.INCOMESTATEMENT?.LESSCOSTOFSALES?.[year] ??
      0;

    const isValid = !isNaN(tradepay) && constOfSales !== 0;

    const result = isValid ? ((tradepay / -constOfSales) * 365).toFixed(2) : 0;

    return result;
  };

  const CashConversionCycle = (year) => {
    const InventoryOutstanding = parseFloat(DaysInventoryOutstanding(year));
    const SalesOutstanding = parseFloat(DaysSalesOutstanding(year));
    const PayableOutstanding = parseFloat(DaysPayableOutstanding(year));

    if (
      isNaN(InventoryOutstanding) ||
      isNaN(SalesOutstanding) ||
      isNaN(PayableOutstanding)
    ) {
      return "0.00";
    }

    const result = InventoryOutstanding + SalesOutstanding - PayableOutstanding;

    return result.toFixed(2);
  };

  // 20 sept 2025
  const currencyOptions = ["HKD", "USD", "EUR", "GBP", "INR", "CNY"];

  return (
    <div className="app-container">
      <Toaster />
      <div className="container-fluid">
        <main className="content">
          <div className="row">
            <Navbar />
            <div className="col-md-10" style={{ marginTop: "30px" }}>
              <NavLink to={`/Dashboard`} className="btn btn-dark ms-2">
                Back to Company Log
              </NavLink>

              <br />
              <br />

              <NavLink
                to={`/details/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Company Information
              </NavLink>

              <NavLink
                to={`/DocumentCheckList/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Document Checklist
              </NavLink>

              <NavLink
                to={`/Searches/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Searches
              </NavLink>

              <NavLink
                to={`/FinancialAnalysis/${id}`}
                className="btn btn-dark ms-2"
              >
                Financial Analysis
              </NavLink>

              <NavLink
                to={`/CompanyScore/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Company Score
              </NavLink>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 style={{ marginTop: "10px" }}>
                  Financial Analysis
                  <span
                    style={{
                      paddingLeft: "20px",
                      color: "red",
                      fontSize: "20px",
                      fontWeight: "bold",
                    }}
                  >
                    {company?.VersionString}
                  </span>
                </h3>
                <div>
                  {isEditing && (
                    <button
                      className="btn btn-secondary me-2"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={() =>
                      isEditing ? handleSubmit() : setIsEditing(true)
                    }
                    disabled={isSubmitting}
                  >
                    {isEditing
                      ? isSubmitting
                        ? "Submitting..."
                        : "Submit"
                      : "Edit"}
                  </button>
                </div>
              </div>

              {loading ? (
                <ShimmerTable row={5} col={5} />
              ) : (
                <>
                  <table className="table table-bordered mt-4">
                    <tbody>
                      <tr>
                        <td>Company Name:</td>
                        <td>{company?.COMPANYINFO?.COMPANYNAME_ENGLISH}</td>
                      </tr>
                      <tr>
                        {/* 21 September 2025 */}
                        <td>Enter Financial Year End</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="date"
                              className="form-control"
                              value={
                                formData?.FINANCIALANALYSIS?.FinDetails
                                  ?.ENTERFINANCIALYEAREND || ""
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  FINANCIALANALYSIS: {
                                    ...prev.FINANCIALANALYSIS,
                                    FinDetails: {
                                      ...prev.FINANCIALANALYSIS?.FinDetails,
                                      ENTERFINANCIALYEAREND: e.target.value,
                                    },
                                  },
                                }))
                              }
                            />
                          ) : (
                            (formData?.FINANCIALANALYSIS?.FinDetails
                              ?.ENTERFINANCIALYEAREND &&
                              formatDateMM(
                                formData.FINANCIALANALYSIS.FinDetails
                                  .ENTERFINANCIALYEAREND
                              )) ||
                            ""
                          )}
                        </td>
                      </tr>

                      <tr>
                        <td>Enter Currency</td>
                        <td>
                          {isEditing ? (
                            <select
                              className="form-control"
                              value={
                                formData?.FINANCIALANALYSIS?.FinDetails
                                  ?.ENTERCURRENCY || ""
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  FINANCIALANALYSIS: {
                                    ...prev.FINANCIALANALYSIS,
                                    FinDetails: {
                                      ...prev.FINANCIALANALYSIS?.FinDetails,
                                      ENTERCURRENCY: e.target.value,
                                    },
                                  },
                                }))
                              }
                            >
                              <option value="">-- Select Currency --</option>
                              {currencyOptions.map((cur) => (
                                <option key={cur} value={cur}>
                                  {cur}
                                </option>
                              ))}
                            </select>
                          ) : (
                            formData?.FINANCIALANALYSIS?.FinDetails
                              ?.ENTERCURRENCY || ""
                          )}
                        </td>
                      </tr>

                      <tr>
                        <td>Unit</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="text"
                              className="form-control"
                              value={
                                formData?.FINANCIALANALYSIS?.FinDetails?.UNIT ||
                                ""
                              }
                              onChange={(e) =>
                                setFormData((prev) => ({
                                  ...prev,
                                  FINANCIALANALYSIS: {
                                    ...prev.FINANCIALANALYSIS,
                                    FinDetails: {
                                      ...prev.FINANCIALANALYSIS?.FinDetails,
                                      UNIT: e.target.value,
                                    },
                                  },
                                }))
                              }
                            />
                          ) : (
                            formData?.FINANCIALANALYSIS?.FinDetails?.UNIT || ""
                          )}
                        </td>
                      </tr>

                      <tr>
                        <td>Scoring Criteria</td>
                        <td>
                          <select
                            className="form-control"
                            value={selectedCriteria}
                            onChange={(e) =>
                              setSelectedCriteria(e.target.value)
                            }
                          >
                            <option value="">
                              -- Select Scoring Criteria --
                            </option>
                            {criteriaFiles.map((name, idx) => (
                              <option key={idx} value={name}>
                                {name}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered mt-4">
                    <thead>
                      <tr>
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#d3e1f2",
                            color: "#000",
                            width: "50%",
                          }}
                        >
                          Enter Auditor Opinion
                        </th>

                        {years.map((year) => (
                          <th key={year}>{auditorOption?.[year] ?? ""}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* 20 Sept 2025*/}
                      <tr>
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#d3e1f2",
                            color: "#000",
                            width: "50%",
                          }}
                        >
                          Enter Auditor Opinion
                        </th>

                        {years.map((year) => (
                          <th key={year}>
                            {isEditing ? (
                              <select
                                className="form-control"
                                value={
                                  formData?.FINANCIALANALYSIS?.Auditor_Option
                                    ?.AUDITOROPINION?.[year] ?? ""
                                }
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    FINANCIALANALYSIS: {
                                      ...prev.FINANCIALANALYSIS,
                                      Auditor_Option: {
                                        ...prev.FINANCIALANALYSIS
                                          ?.Auditor_Option,
                                        AUDITOROPINION: {
                                          ...(prev.FINANCIALANALYSIS
                                            ?.Auditor_Option?.AUDITOROPINION ||
                                            {}),
                                          [year]: e.target.value,
                                        },
                                      },
                                    },
                                  }))
                                }
                              >
                                <option value="">-- Select Opinion --</option>
                                <option value="Unqualified">Unqualified</option>
                                <option value="Qualified">Qualified</option>
                              </select>
                            ) : (
                              formData?.FINANCIALANALYSIS?.Auditor_Option
                                ?.AUDITOROPINION?.[year] || ""
                            )}
                          </th>
                        ))}
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered mt-4">
                    <thead>
                      <tr>
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#0074cc",
                            color: "#ffffff",
                            width: "50%",
                          }}
                        >
                          Income Statement
                        </th>
                        {years.map((year) => (
                          <th
                            key={year}
                            style={{
                              fontWeight: "bold",
                              backgroundColor: "#0074cc",
                              color: "#ffffff",
                              textAlign: "center",
                              width: "10%",
                            }}
                          >
                            {auditorOption?.[year] ?? ""}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map((cat) => (
                        <tr key={cat.key}>
                          <td>{cat.label}</td>
                          {years.map((year) => (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                                backgroundColor: "#fdf3d0",
                              }}
                            >
                              {renderCell(cat.key, year)}
                            </td>
                          ))}
                        </tr>
                      ))}

                      <tr>
                        <th>Gross Profit</th>
                        {years.map((year) => {
                          const value = computeGrossProfit(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>

                      {[
                        "SGANDAEXPENSES",
                        "EXTERNALOPERATINGCOSTS_INCLDISTRIBUTIONRANDDSERVICES",
                        "TAXESOTHERTHANINCOMETAX",
                      ].map((key) => (
                        <tr key={key + "_below_gross_profit"}>
                          <td>
                            {
                              {
                                SGANDAEXPENSES: "SG&A expenses",
                                EXTERNALOPERATINGCOSTS_INCLDISTRIBUTIONRANDDSERVICES:
                                  "External operating costs (incl. distribution, R&D, services)",
                                TAXESOTHERTHANINCOMETAX:
                                  "Taxes other than income tax",
                              }[key]
                            }
                          </td>
                          {years.map((year) => (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {renderCell(key, year)}
                            </td>
                          ))}
                        </tr>
                      ))}

                      <tr>
                        <th>EBITDA</th>
                        {years.map((year) => {
                          const value = computeEBITDA(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>

                      {[
                        "DANDA",
                        "PROVISIONSANDIMPAIRMENTLOSSES",
                        "DISPOSALSOFPPE_GAINSORLOSSES",
                        "DISPOSALSOFINTANGIBLESSUBSIDIARIESANDAFFILIATES_GAINSORLOSSES",
                        "GAINSOR_LOSSESRELATEDTOECLCREDITMODEL",
                        "OTHERNON_RECURRINGINCOMEOREXPENSES",
                        "FINANCECOSTS",
                      ].map((key) => (
                        <tr key={key + "_below_ebitda"}>
                          <td>
                            {
                              {
                                DANDA: "D&A",
                                PROVISIONSANDIMPAIRMENTLOSSES:
                                  "Provisions & impairment losses",
                                DISPOSALSOFPPE_GAINSORLOSSES:
                                  "Disposals of PPE (gains / losses)",
                                DISPOSALSOFINTANGIBLESSUBSIDIARIESANDAFFILIATES_GAINSORLOSSES:
                                  "Disposals of intangibles, subsidiaries, and affiliates (gains / losses)",
                                GAINSOR_LOSSESRELATEDTOECLCREDITMODEL:
                                  "Gains/(Losses) related to ECL credit model",
                                OTHERNON_RECURRINGINCOMEOREXPENSES:
                                  "Other non-recurring income / expenses",
                                FINANCECOSTS: "Finance costs",
                              }[key]
                            }
                          </td>
                          {years.map((year) => (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                                backgroundColor: "#fdf3d0",
                              }}
                            >
                              {renderCell(key, year)}
                            </td>
                          ))}
                        </tr>
                      ))}

                      <tr>
                        <th>Profit / (Loss) before tax</th>
                        {years.map((year) => {
                          const value = computeProfitBeforeTax(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Income tax expense
                        </td>
                        {years.map((year) => (
                          <td
                            key={year}
                            style={{
                              backgroundColor: "#fdf3d0",
                              textAlign: "right",
                              paddingRight: "10px",
                            }}
                          >
                            {renderCell("INCOMETAXEXPENSE", year)}
                          </td>
                        ))}
                      </tr>

                      <tr>
                        <th>Profit / (Loss) for the year</th>
                        {years.map((year) => {
                          const value = computeProfitForYear(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#0074cc",
                            color: "#ffffff",
                            width: "50%",
                          }}
                        >
                          Balance Sheet
                        </th>
                        {years.map((year) => (
                          <th
                            key={year}
                            style={{
                              fontWeight: "bold",
                              backgroundColor: "#0074cc",
                              color: "#ffffff",
                              textAlign: "center",
                              width: "10%",
                            }}
                          >
                            {year}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <b>Non-current Assets</b>
                        </td>
                      </tr>
                      {balanceSheetCategories.map((item) => (
                        <tr key={item.key}>
                          <td style={{ paddingLeft: "20px" }}>{item.label}</td>
                          {years.map((year) => (
                            <td
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                              }}
                            >
                              {renderEditableBalanceCell(item.key, year)}
                            </td>
                          ))}
                        </tr>
                      ))}
                      <tr>
                        <th>Total Non-current Assets</th>
                        {years.map((year) => {
                          const value = computeTotalNonCurrentAssets(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th style={{ width: "50%" }}>Current Assets</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentAssetsCategories.map((item) => (
                        <tr key={item.key}>
                          <td style={{ paddingLeft: "20px" }}>{item.label}</td>
                          {years.map((year) => (
                            <td
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                width: "10%",
                              }}
                            >
                              {renderEditableBalanceCell(item.key, year)}
                            </td>
                          ))}
                        </tr>
                      ))}

                      <tr>
                        <th>Total Current Assets</th>
                        {years.map((year) => {
                          const value = computeTotalCurrentAssets(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th style={{ width: "50%" }}>Current Liabilities</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentLiabilitiesCategories.map((item) => (
                        <tr key={item.key}>
                          <td style={{ paddingLeft: "20px" }}>{item.label}</td>
                          {years.map((year) => (
                            <td
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                width: "10%",
                              }}
                            >
                              {renderEditableBalanceCell(
                                item.key,
                                year,
                                "CURRENTLIABILITIES"
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}

                      <tr>
                        <th>Total Current Liabilities</th>
                        {years.map((year) => {
                          const value = computeTotalCurrentLiabilities(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <th>Net Current Assets</th>
                        {years.map((year) => {
                          const value = computeNetCurrentAssets(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <th>Non Current Liabilities</th>
                        {years.map((_, i) => (
                          <td key={i}></td>
                        ))}
                      </tr>

                      {nonCurrentLiabilitiesCategories.map((item) => (
                        <tr key={item.key}>
                          <td style={{ paddingLeft: "20px" }}>{item.label}</td>
                          {years.map((year) => (
                            <td
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                              }}
                            >
                              {renderEditableBalanceCell(item.key, year)}
                            </td>
                          ))}
                        </tr>
                      ))}

                      <tr>
                        <th>Total Non Current Liabilities</th>
                        {years.map((year) => {
                          const value = computeTotalNonCurrentLiabilities(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <th>Net Assets</th>
                        {years.map((year) => {
                          const value = computeNetAssets(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th style={{ width: "50%" }}>Equity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equityCategories.map((item) => (
                        <tr key={item.key}>
                          <td style={{ paddingLeft: "20px" }}>{item.label}</td>
                          {years.map((year) => (
                            <td
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                width: "10%",
                              }}
                            >
                              {renderEditableBalanceCell(item.key, year)}
                            </td>
                          ))}
                        </tr>
                      ))}

                      <tr>
                        <th style={{ paddingLeft: "20px", fontWeight: "bold" }}>
                          Total Shareholder's Equity
                        </th>
                        {years.map((year) => {
                          const total = computeTotalShareholdersEquity(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(total)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                                fontWeight: "bold",
                                color: "blue",
                              }}
                            >
                              {total < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th
                          style={{
                            paddingLeft: "20px",
                            fontWeight: "bold",
                            width: "50%",
                          }}
                        >
                          Check (should equal "0")
                        </th>
                        {years.map((year) => {
                          const diff = computeCheckDifference(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(diff)
                          );
                          return (
                            <th
                              key={year}
                              style={{
                                textAlign: "right",
                                fontWeight: "bold",
                              }}
                            >
                              {diff === 0
                                ? "0"
                                : diff < 0
                                ? `${formatted}`
                                : formatted}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                  </table>

                  <table className="table table-bordered mt-4">
                    <thead>
                      <tr>
                        <th
                          style={{
                            backgroundColor: "#0074cc",
                            color: "#fff",
                            textAlign: "left",
                            height: "40px",
                            width: "50%",
                          }}
                        >
                          Cashflow Statement
                        </th>
                        {years.map((year) => (
                          <th
                            key={year}
                            style={{
                              backgroundColor: "#0074cc",
                              color: "#fff",
                              textAlign: "center",
                              height: "40px",
                              width: "10%",
                            }}
                          >
                            {year}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th>Profit / (Loss) for the year</th>
                        {years.map((year) => {
                          const value = computeProfitForYear(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(value)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                fontWeight: "bold",
                                color: "blue",
                                textAlign: "right",
                              }}
                            >
                              {value < 0 ? `(${formatted})` : formatted}
                            </td>
                          );
                        })}
                      </tr>
                      <tr>
                        <td
                          style={{ paddingLeft: "20px", fontStyle: "italic" }}
                        >
                          Adjustments for non-cash items
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "30px" }}>+ D&amp;A</td>
                        {/* {years.map((year) => {
                        const value = computeNegativeDA(year);
                        const formatted = formatIndianCurrency(Math.abs(value));
                        return (
                          <td
                            key={year}
                            style={{
                              backgroundColor: "#fdf3d0",
                              textAlign: "right",
                            }}
                          >
                            {value < 0 ? `(${formatted})` : formatted}
                          </td>
                        );
                      })} */}

                        {years.map((year) => {
                          const negativeDA = computeNegativeDA(year);
                          const formattedNegative = formatIndianCurrency(
                            Math.abs(negativeDA)
                          );
                          const isNegative1 = negativeDA < 0;

                          const editableValue =
                            formData?.[`DA_${year}`] ?? computeDA(year);
                          const formattedEditable = formatIndianCurrency(
                            Math.abs(editableValue)
                          );
                          const isNegative2 = editableValue < 0;

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.3",
                              }}
                            >
                              {/* Read-only Negative DA */}
                              <div>
                                {isNegative1
                                  ? `(${formattedNegative})`
                                  : formattedNegative}
                              </div>

                              {/* Editable computeDA */}
                              <div
                                style={{ fontSize: "0.85em", color: "#555" }}
                              >
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`DA_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{ width: "80px" }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formattedEditable})`
                                ) : (
                                  formattedEditable
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "30px" }}>+ Finance costs</td>
                        {/* {years.map((year) => {
                        const value = computeNegativeFC(year);
                        const formatted = formatIndianCurrency(Math.abs(value));
                        return (
                          <td
                            key={year}
                            style={{
                              backgroundColor: "#fdf3d0",
                              textAlign: "right",
                            }}
                          >
                            {value < 0 ? `(${formatted})` : formatted}
                          </td>
                        );
                      })} */}

                        {years.map((year) => {
                          const value1 = computeNegativeFC(year);
                          const formatted1 = formatIndianCurrency(
                            Math.abs(value1)
                          );
                          const isNegative1 = value1 < 0;

                          // Editable FC
                          const defaultValue2 = computeFC(year);
                          const editableValue2 =
                            formData?.[`FC_${year}`] !== undefined
                              ? formData[`FC_${year}`]
                              : defaultValue2;

                          const formatted2 = formatIndianCurrency(
                            Math.abs(editableValue2)
                          );
                          const isNegative2 = editableValue2 < 0;

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.3",
                              }}
                            >
                              {/* Read-only value1 (Negative FC) */}
                              <div>
                                {isNegative1 ? `(${formatted1})` : formatted1}
                              </div>

                              {/* Editable value2 (FC) */}
                              <div style={{ fontSize: "0.85em", color: "red" }}>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue2}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`FC_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{
                                      width: "80px",
                                      padding: "2px",
                                      fontSize: "0.85em",
                                      textAlign: "right",
                                    }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formatted2})`
                                ) : (
                                  formatted2
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "30px" }}>
                          -/+ Gains/(Losses) on impairment or disposal of
                          property, plant and equipment
                        </td>
                        {/* {years.map((year) => {
                        const value = computeNegativeGLI(year);
                        const formatted = formatIndianCurrency(Math.abs(value));
                        return (
                          <td
                            key={year}
                            style={{
                              backgroundColor: "#fdf3d0",
                              textAlign: "right",
                            }}
                          >
                            {value < 0 ? `(${formatted})` : formatted}
                          </td>
                        );
                      })} */}
                        {years.map((year) => {
                          const value1 = computeNegativeGLI(year);
                          const formatted1 = formatIndianCurrency(
                            Math.abs(value1)
                          );
                          const isNegative1 = value1 < 0;

                          // Editable GLI
                          const defaultValue2 = computeGLI(year);
                          const editableValue2 =
                            formData?.[`GLI_${year}`] !== undefined
                              ? formData[`GLI_${year}`]
                              : defaultValue2;

                          const formatted2 = formatIndianCurrency(
                            Math.abs(editableValue2)
                          );
                          const isNegative2 = editableValue2 < 0;

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.3",
                              }}
                            >
                              {/* Read-only value1 (Negative GLI) */}
                              <div>
                                {isNegative1 ? `(${formatted1})` : formatted1}
                              </div>

                              {/* Editable value2 (GLI) */}
                              <div style={{ fontSize: "0.85em", color: "red" }}>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue2}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`GLI_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{
                                      width: "80px",
                                      padding: "2px",
                                      fontSize: "0.85em",
                                      textAlign: "right",
                                    }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formatted2})`
                                ) : (
                                  formatted2
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "30px" }}>
                          -/+ Gains/(Losses) on revaluation or disposal of
                          investments
                        </td>
                        {/* {years.map((year) => {
                        const value = computeNegativeRDI(year);
                        const formatted = formatIndianCurrency(Math.abs(value));
                        return (
                          <td
                            key={year}
                            style={{
                              backgroundColor: "#fdf3d0",
                              textAlign: "right",
                            }}
                          >
                            {value < 0 ? `(${formatted})` : formatted}
                          </td>
                        );
                      })} */}

                        {years.map((year) => {
                          const value1 = computeNegativeRDI(year);
                          const formatted1 = formatIndianCurrency(
                            Math.abs(value1)
                          );
                          const isNegative1 = value1 < 0;

                          // Editable RDI
                          const defaultValue2 = computeRDI(year);
                          const editableValue2 =
                            formData?.[`RDI_${year}`] !== undefined
                              ? formData[`RDI_${year}`]
                              : defaultValue2;

                          const formatted2 = formatIndianCurrency(
                            Math.abs(editableValue2)
                          );
                          const isNegative2 = editableValue2 < 0;

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.3",
                              }}
                            >
                              {/* Read-only value1 (Negative RDI) */}
                              <div>
                                {isNegative1 ? `(${formatted1})` : formatted1}
                              </div>

                              {/* Editable value2 (RDI) */}
                              <div style={{ fontSize: "0.85em", color: "red" }}>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue2}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`RDI_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{
                                      width: "80px",
                                      padding: "2px",
                                      fontSize: "0.85em",
                                      textAlign: "right",
                                    }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formatted2})`
                                ) : (
                                  formatted2
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "30px" }}>
                          -/+ Gains/(Losses) on Exchange Difference
                        </td>
                        {/* {years.map((year) => {
                        const value = computeNegativeED(year);
                        const formatted = formatIndianCurrency(Math.abs(value));
                        return (
                          <td
                            key={year}
                            style={{
                              backgroundColor: "#fdf3d0",
                              textAlign: "right",
                            }}
                          >
                            {value < 0 ? `(${formatted})` : formatted}
                          </td>
                        );
                      })} */}

                        {years.map((year) => {
                          const value1 = computeNegativeED(year);
                          const formatted1 = formatIndianCurrency(
                            Math.abs(value1)
                          );
                          const isNegative1 = value1 < 0;

                          const defaultValue2 = computeED(year);
                          const editableValue2 =
                            formData?.[`ED_${year}`] !== undefined
                              ? formData[`ED_${year}`]
                              : defaultValue2;

                          const formatted2 = formatIndianCurrency(
                            Math.abs(editableValue2)
                          );
                          const isNegative2 = editableValue2 < 0;

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.3",
                              }}
                            >
                              {/* Read-only Negative ED */}
                              <div>
                                {isNegative1 ? `(${formatted1})` : formatted1}
                              </div>

                              {/* Editable ED */}
                              <div style={{ fontSize: "0.85em", color: "red" }}>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue2}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`ED_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{
                                      width: "80px",
                                      padding: "2px",
                                      fontSize: "0.85em",
                                      textAlign: "right",
                                    }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formatted2})`
                                ) : (
                                  formatted2
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <th
                          style={{
                            paddingLeft: "20px",
                            fontWeight: "bold",
                          }}
                        >
                          Cashflow before changes in WCR [FFO]
                        </th>
                        {/* {years.map((year) => {
                        const diff = computeCashflowBeforeExchange(year);
                        const formatted = formatIndianCurrency(Math.abs(diff));
                        return (
                          <th
                            key={year}
                            style={{
                              textAlign: "right",
                              color: "blue",
                            }}
                          >
                            {diff === 0
                              ? "0"
                              : diff < 0
                              ? `(${formatted})`
                              : formatted}
                          </th>
                        );
                      })} */}

                        {years.map((year) => {
                          const value1 = computeCashflowBeforeExchange(year);
                          const formatted1 = formatIndianCurrency(
                            Math.abs(value1)
                          );
                          const isNegative1 = value1 < 0;

                          const defaultValue2 =
                            computeCashflowBeforeExchangeFromData(year);
                          const editableValue2 =
                            formData?.[`CashflowBeforeExchange_${year}`] !==
                            undefined
                              ? formData[`CashflowBeforeExchange_${year}`]
                              : defaultValue2;

                          const formatted2 = formatIndianCurrency(
                            Math.abs(editableValue2)
                          );
                          const isNegative2 = editableValue2 < 0;

                          return (
                            <th
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.2",
                              }}
                            >
                              {/* Read-only value */}
                              <div>
                                {isNegative1 ? `(${formatted1})` : formatted1}
                              </div>

                              {/* Editable input for user-supplied value */}
                              <div style={{ fontSize: "0.85em", color: "red" }}>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue2}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`CashflowBeforeExchange_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{
                                      width: "80px",
                                      padding: "2px",
                                      fontSize: "0.85em",
                                      textAlign: "right",
                                    }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formatted2})`
                                ) : (
                                  formatted2
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>

                      <tr>
                        <td
                          style={{ paddingLeft: "20px", fontStyle: "italic" }}
                        >
                          Adjustments for changes in working capital
                        </td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>

                      <tr>
                        <td
                          style={{
                            paddingLeft: "30px",
                          }}
                        >
                          +/- Changes in AR
                        </td>
                        {/* {years.map((year) => {
                        const diff = computeChangesInAR(year);
                        const formatted = formatIndianCurrency(Math.abs(diff));
                        return (
                          <td
                            key={year}
                            style={{
                              textAlign: "right",
                            }}
                          >
                            {diff === 0
                              ? "0"
                              : diff < 0
                              ? `(${formatted})`
                              : formatted}
                          </td>
                        );
                      })} */}

                        {years.map((year) => {
                          const value1 = computeChangesInAR(year);
                          const formatted1 = formatIndianCurrency(
                            Math.abs(value1)
                          );
                          const isNegative1 = value1 < 0;

                          const defaultValue2 =
                            computeChangesInARFromData(year);
                          const editableValue2 =
                            formData?.[`ChangesInAR_${year}`] !== undefined
                              ? formData[`ChangesInAR_${year}`]
                              : defaultValue2;

                          const formatted2 = formatIndianCurrency(
                            Math.abs(editableValue2)
                          );
                          const isNegative2 = editableValue2 < 0;

                          return (
                            <th
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.2",
                              }}
                            >
                              {/* Computed value (read-only) */}
                              <div>
                                {isNegative1 ? `(${formatted1})` : formatted1}
                              </div>

                              {/* Editable input for provided data */}
                              <div style={{ fontSize: "0.85em", color: "red" }}>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue2}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`ChangesInAR_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{
                                      width: "120px",
                                      padding: "2px",
                                      fontSize: "0.85em",
                                      textAlign: "right",
                                    }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formatted2})`
                                ) : (
                                  formatted2
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>

                      <tr>
                        <td
                          style={{
                            paddingLeft: "30px",
                          }}
                        >
                          +/- Changes in AP
                        </td>
                        {/* {years.map((year) => {
                        const diff = computeChangesInCL(year);
                        const formatted = formatIndianCurrency(Math.abs(diff));
                        return (
                          <td
                            key={year}
                            style={{
                              textAlign: "right",
                            }}
                          >
                            {diff === 0
                              ? "0"
                              : diff < 0
                              ? `(${formatted})`
                              : formatted}
                          </td>
                        );
                      })} */}

                        {years.map((year) => {
                          const value1 = computeChangesInCL(year);
                          const isNegative1 = value1 < 0;
                          const formatted1 = formatIndianCurrency(
                            Math.abs(value1)
                          );

                          const defaultValue2 =
                            computeChangesInCLFromData(year);
                          const editableValue2 =
                            formData?.[`ChangesInCL_${year}`] !== undefined
                              ? formData[`ChangesInCL_${year}`]
                              : defaultValue2;
                          const isNegative2 = editableValue2 < 0;
                          const formatted2 = formatIndianCurrency(
                            Math.abs(editableValue2)
                          );

                          return (
                            <th
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.2",
                              }}
                            >
                              {/* Computed (read-only) */}
                              <div>
                                {isNegative1 ? `(${formatted1})` : formatted1}
                              </div>

                              {/* Editable or read-only input */}
                              <div style={{ fontSize: "0.85em", color: "red" }}>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue2}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`ChangesInCL_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{
                                      width: "120px",
                                      padding: "2px",
                                      fontSize: "0.85em",
                                      textAlign: "right",
                                    }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formatted2})`
                                ) : (
                                  formatted2
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>

                      <tr>
                        <th
                          style={{
                            paddingLeft: "30px",
                          }}
                        >
                          Cashflow from operating activities
                        </th>
                        {/* {years.map((year) => {
                        const diff = computeARandAP(year);
                        const formatted = formatIndianCurrency(Math.abs(diff));
                        return (
                          <th
                            key={year}
                            style={{
                              textAlign: "right",
                              color: "blue",
                            }}
                          >
                            {diff === 0
                              ? "0"
                              : diff < 0
                              ? `(${formatted})`
                              : formatted}
                          </th>
                        );
                      })} */}

                        {/* {years.map((year) => {
                        const value1 = computeARandAP(year);
                        const value2 = computeARandAPFromData(year);

                        const formatted1 = formatIndianCurrency(
                          Math.abs(value1)
                        );
                        const formatted2 = formatIndianCurrency(
                          Math.abs(value2)
                        );

                        return (
                          <th
                            key={year}
                            style={{
                              backgroundColor: "#fdf3d0",
                              textAlign: "right",
                              padding: "4px",
                              lineHeight: "1.2",
                            }}
                          >
                            <div>
                              {value1 < 0 ? `(${formatted1})` : formatted1}
                            </div>
                            <div style={{ fontSize: "0.85em", color: "red" }}>
                              {value2 < 0 ? `(${formatted2})` : formatted2}
                            </div>
                          </th>
                        );
                      })} */}

                        {years.map((year) => {
                          const value1 = computeARandAP(year);
                          const isNegative1 = value1 < 0;
                          const formatted1 = formatIndianCurrency(
                            Math.abs(value1)
                          );

                          const defaultValue2 = computeARandAPFromData(year);
                          const editableValue2 =
                            formData?.[`ARandAP_${year}`] !== undefined
                              ? formData[`ARandAP_${year}`]
                              : defaultValue2;
                          const isNegative2 = editableValue2 < 0;
                          const formatted2 = formatIndianCurrency(
                            Math.abs(editableValue2)
                          );

                          return (
                            <th
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.2",
                              }}
                            >
                              {/* Computed value (value1) */}
                              <div>
                                {isNegative1 ? `(${formatted1})` : formatted1}
                              </div>

                              {/* Editable or read-only field for value2 */}
                              <div style={{ fontSize: "0.85em", color: "red" }}>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue2}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`ARandAP_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{
                                      width: "120px",
                                      padding: "2px",
                                      fontSize: "0.85em",
                                      textAlign: "right",
                                    }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formatted2})`
                                ) : (
                                  formatted2
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>

                      {Object.entries(cashflowCategories).map(
                        ([key, label]) => (
                          <tr key={key}>
                            <td style={{ paddingLeft: "40px" }}>{label}</td>
                            {years.map((year) => (
                              <td
                                key={year}
                                style={{
                                  backgroundColor: "#fdf3d0",
                                  textAlign: "right",
                                }}
                              >
                                {formatIndianCurrency(
                                  formData?.FINANCIALANALYSIS
                                    ?.CASHFLOWSTATEMENT?.[key]?.[year] ?? 0
                                )}
                              </td>
                            ))}
                          </tr>
                        )
                      )}

                      <tr>
                        <th
                          style={{
                            paddingLeft: "30px",
                          }}
                        >
                          Cashflow from investing activities
                        </th>
                        {years.map((year) => {
                          const diff =
                            computeCashflowFromInvestingActivities(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(diff)
                          );
                          return (
                            <th
                              key={year}
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {diff === 0
                                ? "0"
                                : diff < 0
                                ? `(${formatted})`
                                : formatted}
                            </th>
                          );
                        })}
                      </tr>

                      {Object.entries(cashflowFromFinancingActivities).map(
                        ([key, label]) => (
                          <tr key={key}>
                            <td style={{ paddingLeft: "40px" }}>{label}</td>
                            {[2022, 2023, 2024, 2025].map((year) => (
                              <td
                                key={year}
                                style={{
                                  backgroundColor: "#fdf3d0",
                                  textAlign: "right",
                                }}
                              >
                                {formatIndianCurrency(
                                  formData?.FINANCIALANALYSIS
                                    ?.CASHFLOWSTATEMENT?.[key]?.[year] ?? 0
                                )}
                              </td>
                            ))}
                          </tr>
                        )
                      )}

                      <tr>
                        <td></td>
                        {years.map((year) => {
                          const diff =
                            computeCashflowFromFinancingActivities(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(diff)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                                fontWeight: "bold",
                              }}
                            >
                              {diff === 0
                                ? "0"
                                : diff < 0
                                ? `(${formatted})`
                                : formatted}
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <th>Cashflow for the year</th>
                        {/* {years.map((year) => {
                        const diff = CashflowForTheYear(year);
                        const formatted = formatIndianCurrency(Math.abs(diff));
                        return (
                          <th
                            key={year}
                            style={{
                              textAlign: "right",
                              fontWeight: "bold",
                              color: "blue",
                            }}
                          >
                            {diff === 0
                              ? "0"
                              : diff < 0
                              ? `(${formatted})`
                              : formatted}
                          </th>
                        );
                      })} */}

                        {/* {years.map((year) => {
                        const value1 = CashflowForTheYear(year);
                        const value2 = CashflowForTheYearFromData(year);

                        const formatted1 = formatIndianCurrency(
                          Math.abs(value1)
                        );
                        const formatted2 = formatIndianCurrency(
                          Math.abs(value2)
                        );

                        return (
                          <th
                            key={year}
                            style={{
                              backgroundColor: "#fdf3d0",
                              textAlign: "right",
                              padding: "4px",
                              lineHeight: "1.2",
                            }}
                          >
                            <div>
                              {value1 < 0 ? `(${formatted1})` : formatted1}
                            </div>
                            <div style={{ fontSize: "0.85em", color: "red" }}>
                              {value2 < 0 ? `(${formatted2})` : formatted2}
                            </div>
                          </th>
                        );
                      })} */}

                        {years.map((year) => {
                          const value1 = CashflowForTheYear(year);
                          const isNegative1 = value1 < 0;
                          const formatted1 = formatIndianCurrency(
                            Math.abs(value1 ?? 0)
                          );

                          const defaultValue2 =
                            CashflowForTheYearFromData(year);
                          const editableValue2 =
                            formData?.[`Cashflow_${year}`] !== undefined
                              ? formData[`Cashflow_${year}`]
                              : defaultValue2;

                          const isNegative2 = editableValue2 < 0;
                          const formatted2 = formatIndianCurrency(
                            Math.abs(editableValue2 ?? 0)
                          );

                          return (
                            <th
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.2",
                              }}
                            >
                              {/* Computed (read-only) */}
                              <div>
                                {isNegative1 ? `(${formatted1})` : formatted1}
                              </div>

                              {/* Editable or read-only input */}
                              <div style={{ fontSize: "0.85em", color: "red" }}>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue2}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`Cashflow_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{
                                      width: "120px",
                                      padding: "2px",
                                      fontSize: "0.85em",
                                      textAlign: "right",
                                    }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formatted2})`
                                ) : (
                                  formatted2
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>

                      <tr>
                        <td>Change in Cash Balances</td>
                        {/* {years.map((year) => {
                        const diff = computeChangesInCashAndBankBalances(year);
                        const formatted = formatIndianCurrency(Math.abs(diff));
                        return (
                          <td
                            key={year}
                            style={{
                              textAlign: "right",
                            }}
                          >
                            {diff === 0
                              ? "0"
                              : diff < 0
                              ? `(${formatted})`
                              : formatted}
                          </td>
                        );
                      })} */}

                        {/* {years.map((year) => {
                        const value1 =
                          computeChangesInCashAndBankBalances(year);
                        const value2 =
                          computeChangesInCashAndBankBalancesFromData(year);

                        const formatted1 = formatIndianCurrency(
                          Math.abs(value1)
                        );
                        const formatted2 = formatIndianCurrency(
                          Math.abs(value2)
                        );

                        return (
                          <th
                            key={year}
                            style={{
                              backgroundColor: "#fdf3d0",
                              textAlign: "right",
                              padding: "4px",
                              lineHeight: "1.2",
                            }}
                          >
                            <div>
                              {value1 < 0 ? `(${formatted1})` : formatted1}
                            </div>
                            <div style={{ fontSize: "0.85em", color: "red" }}>
                              {value2 < 0 ? `(${formatted2})` : formatted2}
                            </div>
                          </th>
                        );
                      })} */}

                        {years.map((year) => {
                          const value1 =
                            computeChangesInCashAndBankBalances(year);
                          const isNegative1 = value1 < 0;
                          const formatted1 = formatIndianCurrency(
                            Math.abs(value1 ?? 0)
                          );

                          const defaultValue2 =
                            computeChangesInCashAndBankBalancesFromData(year);
                          const editableValue2 =
                            formData?.[
                              `ChangesInCashAndBankBalances_${year}`
                            ] !== undefined
                              ? formData[`ChangesInCashAndBankBalances_${year}`]
                              : defaultValue2;

                          const isNegative2 = editableValue2 < 0;
                          const formatted2 = formatIndianCurrency(
                            Math.abs(editableValue2 ?? 0)
                          );

                          return (
                            <th
                              key={year}
                              style={{
                                backgroundColor: "#fdf3d0",
                                textAlign: "right",
                                padding: "4px",
                                lineHeight: "1.2",
                              }}
                            >
                              {/* Computed (read-only) */}
                              <div>
                                {isNegative1 ? `(${formatted1})` : formatted1}
                              </div>

                              {/* Editable or read-only input */}
                              <div style={{ fontSize: "0.85em", color: "red" }}>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    value={editableValue2}
                                    onChange={(e) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        [`ChangesInCashAndBankBalances_${year}`]:
                                          parseFloat(e.target.value) || 0,
                                      }))
                                    }
                                    style={{
                                      width: "120px",
                                      padding: "2px",
                                      fontSize: "0.85em",
                                      textAlign: "right",
                                    }}
                                  />
                                ) : isNegative2 ? (
                                  `(${formatted2})`
                                ) : (
                                  formatted2
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#0074cc",
                            color: "#ffffff",
                            width: "40%",
                          }}
                        >
                          Ratios
                        </th>
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#0074cc",
                            color: "#ffffff",
                            width: "10%",
                          }}
                        >
                          Reference Range
                        </th>
                        {years.map((year) => (
                          <th
                            key={year}
                            style={{
                              fontWeight: "bold",
                              backgroundColor: "#0074cc",
                              color: "#ffffff",
                              textAlign: "center",
                              width: "8%",
                            }}
                          >
                            {year}
                          </th>
                        ))}
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#0074cc",
                            color: "#ffffff",
                            textAlign: "center",
                            width: "8%",
                          }}
                        >
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Revenue Change %
                        </td>
                        <td></td>
                        {years.map((year) => {
                          const rate = computeRevenueGrowthRate(year);
                          const numericValue = parseFloat(rate);
                          const bgColor =
                            numericValue < 0 ? "#f6c9ce" : "#ffffff";
                          const textColor =
                            numericValue < 0 ? "#a83f40" : "#000000";
                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: bgColor,
                                textAlign: "right",
                                color: textColor,
                              }}
                            >
                              {rate}
                            </td>
                          );
                        })}
                        <td
                          style={{
                            backgroundColor: "#f6c9ce",
                            textAlign: "center",
                          }}
                        >
                          value &lt; 0
                        </td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>CAGR %</td>
                        <td></td>
                        {years.map((year) => {
                          const value = computeSquareRootRevenueGrowth(year);
                          const numeric = parseFloat(value);
                          const bgColor = numeric < 0 ? "#f6c9ce" : "#ffffff";
                          const textColor = numeric < 0 ? "#a83f40" : "#000000";

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: bgColor,
                                textAlign: "right",
                                color: textColor,
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Gross Profit Margin
                        </td>
                        <td></td>
                        {years.map((year) => {
                          const value = GrossProfitMargin(year);
                          const numeric = parseFloat(value);
                          const bgColor = numeric < 0 ? "#f6c9ce" : "#ffffff";
                          const textColor = numeric < 0 ? "#a83f40" : "#000000";

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: bgColor,
                                textAlign: "right",
                                color: textColor,
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td
                          style={{
                            backgroundColor: "#f6c9ce",
                            textAlign: "center",
                          }}
                        >
                          value &lt; 0
                        </td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Average Gross Profit Margin
                        </td>
                        <td></td>
                        {(() => {
                          const avgGPMs = computeExcelStyleAverageGPM(years);

                          return years.map((year, idx) => {
                            const avgGPM = avgGPMs[idx];
                            const numericValue = parseFloat(avgGPM);
                            const bgColor =
                              numericValue < 0 ? "#ffcccc" : "#ffffff";

                            return (
                              <td
                                key={year}
                                style={{
                                  backgroundColor: bgColor,
                                  textAlign: "right",
                                }}
                              >
                                {avgGPM}
                              </td>
                            );
                          });
                        })()}
                        <td
                          style={{
                            backgroundColor: "#f6c9ce",
                            textAlign: "center",
                          }}
                        >
                          value &lt; 0
                        </td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>EBITDA Margin</td>
                        <td></td>
                        {years.map((year) => {
                          const value = ebitdaMargin(year);
                          const numeric = parseFloat(value);
                          const bgColor = numeric < 0 ? "#f6c9ce" : "#ffffff";
                          const textColor = numeric < 0 ? "#a83f40" : "#000000";

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: bgColor,
                                textAlign: "right",
                                color: textColor,
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td
                          style={{
                            backgroundColor: "#f6c9ce",
                            textAlign: "center",
                          }}
                        >
                          value &lt; 0
                        </td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Net Profit Margin
                        </td>
                        <td></td>
                        {years.map((year) => {
                          const value = NetProfitMargin(year);
                          const numeric = parseFloat(value);
                          const bgColor = numeric < 0 ? "#f6c9ce" : "#ffffff";
                          const textColor = numeric < 0 ? "#a83f40" : "#000000";

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: bgColor,
                                textAlign: "right",
                                color: textColor,
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td
                          style={{
                            backgroundColor: "#f6c9ce",
                            textAlign: "center",
                          }}
                        >
                          value &lt; 0
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#7eabcc",
                            color: "#ffffff",
                            width: "40%",
                          }}
                        >
                          Liquidity
                        </th>
                        <td
                          style={{
                            backgroundColor: "#7eabcc",
                            width: "10%",
                          }}
                        ></td>
                        {years.map((year) => (
                          <th
                            key={year}
                            style={{
                              fontWeight: "bold",
                              backgroundColor: "#7eabcc",
                              color: "#ffffff",
                              textAlign: "center",
                              width: "8%",
                            }}
                          >
                            {year}
                          </th>
                        ))}
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#7eabcc",
                            color: "#ffffff",
                            textAlign: "center",
                            width: "8%",
                          }}
                        ></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ paddingLeft: "20px" }}>Current Ratio</td>
                        <td style={{ backgroundColor: "#fdf3d0" }}>&gt; 1.5</td>
                        {years.map((year) => {
                          const value = CurrentRatio(year);
                          const numeric = parseFloat(value);
                          const bgColor = numeric < 0 ? "#f6c9ce" : "#ffffff";
                          const textColor = numeric < 0 ? "#a83f40" : "#000000";

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: bgColor,
                                textAlign: "right",
                                color: textColor,
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>Quick Ratio</td>
                        <td style={{ backgroundColor: "#fdf3d0" }}>&gt; 1.5</td>
                        {years.map((year) => {
                          const value = QuickRatio(year);
                          const numeric = parseFloat(value);
                          const bgColor = numeric < 0 ? "#f6c9ce" : "#ffffff";
                          const textColor = numeric < 0 ? "#a83f40" : "#000000";

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: bgColor,
                                textAlign: "right",
                                color: textColor,
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Current Assets from Related Parties
                        </td>
                        <td style={{ backgroundColor: "#fdf3d0" }}>&lt; 10</td>
                        {years.map((year) => {
                          const value = CurrentAssetsfromRelatedParties(year);
                          const numeric = parseFloat(value);
                          const bgColor = numeric < 0 ? "#f6c9ce" : "#ffffff";
                          const textColor = numeric < 0 ? "#a83f40" : "#000000";

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: bgColor,
                                textAlign: "right",
                                color: textColor,
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Cashflow from Operating Activities
                        </td>
                        <td style={{ backgroundColor: "#fdf3d0" }}>&gt; 0</td>
                        {years.map((year) => {
                          const diff = computeARandAP(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(diff)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {diff === 0
                                ? "0"
                                : diff < 0
                                ? `(${formatted})`
                                : formatted}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#7eabcc",
                            color: "#ffffff",
                            width: "40%",
                          }}
                        >
                          Leverage
                        </th>
                        <td
                          style={{
                            backgroundColor: "#7eabcc",
                            width: "10%",
                          }}
                        ></td>
                        {years.map((year) => (
                          <th
                            key={year}
                            style={{
                              fontWeight: "bold",
                              backgroundColor: "#7eabcc",
                              color: "#ffffff",
                              textAlign: "center",
                              width: "8%",
                            }}
                          >
                            {year}
                          </th>
                        ))}

                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#7eabcc",
                            color: "#ffffff",
                            textAlign: "center",
                            width: "8%",
                          }}
                        >
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Debt / Equity Ratio
                        </td>
                        <td style={{ backgroundColor: "#fdf3d0" }}>&lt; 2.0</td>
                        {years.map((year) => {
                          const diff = DebtEquityRatio(year);
                          const formatted = formatIndianCurrency(
                            Math.abs(diff)
                          );
                          return (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {diff === 0
                                ? "0"
                                : diff < 0
                                ? `(${formatted})`
                                : formatted}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Net Debt / EBITDA Ratio
                        </td>
                        <td style={{ backgroundColor: "#fdf3d0" }}>&lt; 5</td>
                        {years.map((year) => {
                          const value = NetDebtEBITDARatio(year);
                          const numeric = parseFloat(value);
                          const bgColor = numeric > 1.8 ? "#f6c9ce" : "#ffffff";
                          const textColor =
                            numeric > 1.8 ? "#a83f40" : "#000000";

                          return (
                            <td
                              key={year}
                              style={{
                                backgroundColor: bgColor,
                                textAlign: "right",
                                color: textColor,
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td
                          style={{
                            backgroundColor: "#f6c9ce",
                            textAlign: "center",
                          }}
                        >
                          value &gt; 1.8
                        </td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Debt / Assets Ratio
                        </td>
                        <td style={{ backgroundColor: "#fdf3d0" }}>&lt; 1.0</td>
                        {years.map((year) => {
                          const value = DebtAssetsRatio(year);
                          return (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Interest Coverage Ratio
                        </td>
                        <td style={{ backgroundColor: "#fdf3d0" }}>&gt; 2.0</td>
                        {years.map((year) => {
                          const value = InterestCoverageRatio(year);

                          return (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </tbody>
                  </table>

                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#7eabcc",
                            color: "#ffffff",
                            width: "40%",
                          }}
                        >
                          Cash Conversion
                        </th>
                        <td
                          style={{
                            backgroundColor: "#7eabcc",
                            width: "10%",
                          }}
                        ></td>
                        {years.map((year) => (
                          <th
                            key={year}
                            style={{
                              fontWeight: "bold",
                              backgroundColor: "#7eabcc",
                              color: "#ffffff",
                              textAlign: "center",
                              width: "8%",
                            }}
                          >
                            {year}
                          </th>
                        ))}
                        <th
                          style={{
                            fontWeight: "bold",
                            backgroundColor: "#7eabcc",
                            color: "#ffffff",
                            textAlign: "center",
                            width: "8%",
                          }}
                        >
                          Notes
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Days Inventory Outstanding
                        </td>
                        <td style={{ backgroundColor: "#fdf3d0" }}></td>
                        {years.map((year) => {
                          const value = DaysInventoryOutstanding(year);

                          return (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Days Sales Outstanding
                        </td>
                        <td style={{ backgroundColor: "#fdf3d0" }}></td>
                        {years.map((year) => {
                          const value = DaysSalesOutstanding(year);

                          return (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Days Payable Outstanding
                        </td>
                        <td style={{ backgroundColor: "#fdf3d0" }}></td>
                        {years.map((year) => {
                          const value = DaysPayableOutstanding(year);

                          return (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>

                      <tr>
                        <td style={{ paddingLeft: "20px" }}>
                          Cash Conversion Cycle
                        </td>
                        <td style={{ backgroundColor: "#fdf3d0" }}>&lt; 90</td>
                        {years.map((year) => {
                          const value = CashConversionCycle(year);

                          return (
                            <td
                              key={year}
                              style={{
                                textAlign: "right",
                              }}
                            >
                              {value}
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
